// The parts of Settings, each its own page with its own link (#/settings/people): Riverstone's tabs, and
// Blueprint's Setup pages (the setup audit, 24 Oct: blueprint/places.ts groups them on the rail). Who
// opens a part is Settings' own check, by permission: Technicians' iPads for who edits settings, Ads and tracking numbers
// for who sees marketing, Job forms for who edits the org, Payroll rules for the owner, managers and the
// bookkeeper; the rest for everyone who opens Settings.
import { settingsRights, type SettingsPage } from './settings-model.ts';
import { setupRights } from './setup-model.ts';

export type { SettingsPage };

export interface SettingsPageDef {
  page: SettingsPage;
  /** Riverstone's tab; none for a page only Blueprint has. */
  tab?: string;
  /** Blueprint's name for it, on the Setup rail and as its title; none for Riverstone's Rules & defaults. */
  label?: string;
  /** Who opens it, by the permissions they hold; everyone who opens Settings when absent. */
  may?: (held: readonly string[]) => boolean;
}

// payroll's rules are read where pay is (accounting.view): the owner changes them, a manager the late-time flag
const payrollReaders = (held: readonly string[]) => held.includes('accounting.view');
// agents.view: the owner and managers (agents/Agents.tsx)
const seesAgents = (held: readonly string[]) => held.includes('agents.view');

// In Riverstone's tab order, then the pages Blueprint alone has.
export const SETTINGS_PAGES: readonly SettingsPageDef[] = [
  // Blueprint shares its rules out among the pages they govern (RULE_SECTIONS)
  { page: 'rules', tab: 'Rules & defaults' },
  { page: 'roles', tab: 'Roles & permissions', label: 'Who may do what' },
  { page: 'people', tab: 'People', label: 'People' },
  { page: 'time', tab: 'Time off & holidays', label: 'Hours and holidays' },
  {
    page: 'devices',
    tab: 'Devices',
    label: 'Technicians’ iPads',
    may: (r) => settingsRights(r).edit,
  },
  { page: 'pricebook', tab: 'Price book', label: 'Price book' },
  { page: 'org', tab: 'Departments & job types', label: 'Job types' },
  { page: 'tax', tab: 'Tax zones', label: 'Sales tax' },
  { page: 'templates', tab: 'Message templates', label: 'Texts and email' },
  { page: 'calls', tab: 'Call reasons', label: 'Calls' },
  // marketing.view and .manage are the owner's and managers' (BL-008)
  {
    page: 'campaigns',
    tab: 'Campaigns',
    label: 'Ads and tracking numbers',
    may: (r) => setupRights(r).seesMarketing,
  },
  // the form builder is the owner's and managers' (org.edit): nobody else opens it
  { page: 'forms', tab: 'Job forms', label: 'Job forms', may: (r) => setupRights(r).org },
  { page: 'booking', label: 'Booking and dispatch' },
  { page: 'plans', label: 'Membership plans' },
  { page: 'invoices', label: 'Invoices and payments' },
  // as Rules & defaults was: managers change the margin we aim for, everyone reads them
  { page: 'costs', label: 'Costs and margins' },
  { page: 'payroll', label: 'Payroll rules', may: payrollReaders },
  { page: 'reviews', label: 'Reviews and ratings' },
  // what each agent may do, its budget and notes (Inbox's Agent settings): those who see the agents
  { page: 'agents', label: 'AI agents', may: seesAgents },
  // QuickBooks or Xero: who opens the books; the owner and the bookkeeper connect it (accounting.setup)
  { page: 'connections', label: 'Connected systems', may: payrollReaders },
];

/** A part's name in this design: Riverstone's tab, or Blueprint's page. */
export const pageName = (p: SettingsPageDef, bp: boolean): string | undefined => (bp ? p.label : p.tab);

/** The parts these permissions open in this design, in Riverstone's tab order. */
export const settingsPagesFor = (held: readonly string[], bp = false): SettingsPageDef[] =>
  SETTINGS_PAGES.filter((p) => pageName(p, bp) !== undefined && (!p.may || p.may(held)));

export const isSettingsPage = (s: string | undefined): s is SettingsPage =>
  SETTINGS_PAGES.some((p) => p.page === s);

/** A Blueprint page's definition, by its part. */
export const settingsPage = (page: SettingsPage): SettingsPageDef =>
  SETTINGS_PAGES.find((p) => p.page === page)!;
